import { useRef, useCallback } from 'react'
import { toast } from 'react-toastify'
import { getAttachmentAdditionError } from '../constants/fileTypes'
import { Attachment } from '../components/Attachments/AttachmentList'

interface UseFileAttachmentsOptions {
  allowedTypes: readonly string[]
  maxFileSize: number
  maxTotalSize?: number
  onAdd?: (newAttachments: Attachment[]) => void
}

export const useFileAttachments = (options: UseFileAttachmentsOptions) => {
  const {
    allowedTypes,
    maxFileSize,
    maxTotalSize = maxFileSize,
    onAdd,
  } = options

  const fileInputRef = useRef<HTMLInputElement>(null)

  const openFileSelect = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const validateAndAddFiles = useCallback(
    (
      // Accepts a plain File array too, so programmatic attach paths (e.g.
      // generated spec-sheet PDFs) share this validation without a FileList.
      files: FileList | readonly File[] | null,
      currentAttachments: Attachment[] = []
    ): Attachment[] | null => {
      if (!files) return null

      const filesArr = Array.from(files)

      const error = getAttachmentAdditionError(filesArr, {
        allowedTypes,
        maxFileSize,
        maxTotalSize,
        currentTotalSize: currentAttachments.reduce(
          (sum, att) => sum + att.size,
          0
        ),
      })
      if (error) {
        toast.error(error)
        return null
      }

      // Create attachment objects
      const newAttachments: Attachment[] = filesArr.map((file) => ({
        file,
        filename: file.name,
        size: file.size,
      }))

      toast.success(
        `${filesArr.length} file${filesArr.length === 1 ? '' : 's'} attached`
      )
      return newAttachments
    },
    [allowedTypes, maxFileSize, maxTotalSize]
  )

  const handleFileSelect = useCallback(
    (
      e: React.ChangeEvent<HTMLInputElement>,
      currentAttachments: Attachment[] = []
    ) => {
      e.preventDefault()
      const files = e.target.files
      const newAttachments = validateAndAddFiles(files, currentAttachments)

      if (newAttachments) {
        onAdd?.(newAttachments)
      }

      // Reset input to allow re-selecting the same file
      e.target.value = ''
    },
    [validateAndAddFiles, onAdd]
  )

  const createRemoveHandler = useCallback(
    (onRemove: (index: number) => void) => (index: number) => {
      onRemove(index)
    },
    []
  )

  return {
    fileInputRef,
    openFileSelect,
    handleFileSelect,
    validateAndAddFiles,
    createRemoveHandler,
  }
}
